import { useRef, useState } from "react";
import { Link } from "react-router";
import {
  Receipt,
  Plus,
  ArrowUpRight,
  History,
  BarChart3,
} from "lucide-react";
import { PageSection } from "@/components/ui/page-section";
import { PageJumpNav, type PageJumpLink } from "@/components/ui/page-jump-nav";
import { DashboardStats } from "@/components/dashboard/dashboard-stats";
import { TransactionsTable } from "@/components/dashboard/transactions-table";
import { TopUpHistory } from "@/components/wallet/top-up-history";
import { TopUpModal } from "@/components/wallet/top-up-modal";
import { useAppContext } from "@/providers";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ErrorMessage } from "@/components/ui/error-message";
import { t } from "@/lib/i18n/messages";
import type { TransactionType } from "@/types/wallet";
import { cn } from "@/lib/utils";

// ─── Jump nav links for Wallet page ────────────────────────────────────────

const WALLET_NAV_LINKS: PageJumpLink[] = [
  { id: "top-up-history", num: "01", ar: "سجل الشحن", en: "Top-Up History" },
  { id: "ledger", num: "02", ar: "كشف المعاملات", en: "Ledger" },
];

export default function DashboardPage() {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const {
    walletBalance: data,
    loading,
    walletError: error,
    refetch,
  } = useDashboardData();

  // Table scroll / highlight state
  const tableRef = useRef<HTMLDivElement>(null);
  const [highlightedId, setHighlightedId] = useState<string | undefined>(
    undefined,
  );

  // Filter table by transaction type when stat card is clicked
  const [filterType, setFilterType] = useState<TransactionType | undefined>(
    undefined,
  );
  const handleFilterByType = (type: TransactionType | undefined) => {
    setFilterType(type);
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Top-up modal state
  const [showTopUp, setShowTopUp] = useState(false);

  return (
    <>
      {/* ─── 00 · HERO — wallet balance + headline stats ───────────────── */}
      <section className="w-full border-b border-border bg-card relative overflow-hidden">
        <div className="relative px-4 md:px-8 py-6 md:py-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {isRTL ? "محفظة الشريك" : "Partner Wallet"}
              </h1>
              <p className="text-sm text-foreground-secondary mt-0.5">
                {isRTL
                  ? "إدارة الأرصدة، شحن المحفظة، ومتابعة سجل العمليات المالية."
                  : "Manage balances, top up funds, and inspect transaction ledgers."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard/analytics"
                className={cn(
                  "inline-flex items-center gap-2 h-10 px-4 rounded-full border transition-all",
                  "text-[13px] font-semibold border-border bg-background hover:bg-muted text-foreground",
                  "shadow-xs hover:border-primary/40",
                )}
              >
                <BarChart3 className="w-4 h-4 text-primary" />
                <span>{isRTL ? "عرض التحليلات" : "View Analytics"}</span>
                <ArrowUpRight className={cn("w-3.5 h-3.5 text-foreground-secondary", isRTL && "rotate-[-90deg]")} />
              </Link>

              <button
                type="button"
                onClick={() => setShowTopUp(true)}
                className={cn(
                  "inline-flex items-center gap-2 h-10 px-5 rounded-full border transition-all active:scale-[0.97]",
                  "text-[13px] font-bold shadow-sm",
                  "bg-primary text-white border-primary hover:bg-primary-dark",
                )}
              >
                <Plus className="w-4 h-4" strokeWidth={2.2} />
                <span>{isRTL ? "شحن المحفظة" : "Top up wallet"}</span>
              </button>
            </div>
          </div>

          <DashboardStats
            locale={locale}
            data={data}
            loading={loading}
            error={error}
            refetch={refetch}
            onFilterByType={handleFilterByType}
          />
        </div>
      </section>

      {/* ─── Sticky in-page jump nav ──────────────────────────────────── */}
      <PageJumpNav isRTL={isRTL} links={WALLET_NAV_LINKS} />

      {/* ─── Body ─────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10 space-y-10 md:space-y-12">
        {loading && !data && (
          <div className="my-8">
            <LoadingSpinner size="lg" label={t(locale, "loading")} />
          </div>
        )}

        {error && !data && (
          <div className="my-8">
            <ErrorMessage
              title={t(locale, "error")}
              message={error}
              onRetry={refetch}
              retryLabel={t(locale, "retry")}
            />
          </div>
        )}

        {/* ── 01 · TOP-UP HISTORY ─────────────────────────────────────── */}
        <PageSection
          id="top-up-history"
          num="01"
          icon={History}
          title={isRTL ? "سجل عمليات الشحن" : "Top-up History"}
          subtitle={
            isRTL
              ? "متابعة طلبات الإيداع وحالات الدفع والمبالغ المضافة للمحفظة."
              : "Track top-up requests, payment status, and deposited funds."
          }
        >
          <TopUpHistory locale={locale} />
        </PageSection>

        {/* ── 02 · LEDGER — full transactions table ───────────────────── */}
        <PageSection
          id="ledger"
          num="02"
          icon={Receipt}
          title={isRTL ? "سجل المعاملات والعمليات" : "Transaction Ledger"}
          subtitle={
            isRTL
              ? "كشف الحساب الكامل، تصفية بحسب النوع، والبحث وتصدير التقارير."
              : "Full financial ledger with type filtering, searching, and CSV export."
          }
        >
          <div ref={tableRef}>
            <TransactionsTable
              locale={locale}
              highlightedId={highlightedId}
              filterType={filterType}
            />
          </div>
        </PageSection>
      </div>

      {showTopUp && <TopUpModal onClose={() => setShowTopUp(false)} />}
    </>
  );
}
